import {
  coordinateInMapRegion,
  getCoordinateRegionDistanceRatio,
} from '@components/Explorer/MapGeometry';
import type { MapMarkerProfile } from '@components/Explorer/Markers';
import { isDatePast } from '@helpers/Dates';
import { isValidMapCoordinate } from '@helpers/Map';
import type { MapRegion } from '@helpers/Map';
import { Plan, User } from '@types';

const VIEWPORT_PADDING_RATIO = 0.15;
const DEFAULT_PAGE_SIZE = 60;
const DAY_IN_MS = 1000 * 60 * 60 * 24;
const START_DATE_FRESHNESS_DAYS = 90;
const CREATED_AT_FRESHNESS_DAYS = 30;
const START_DATE_FRESHNESS_WEIGHT = 0.4;
const CREATED_AT_FRESHNESS_WEIGHT = 0.6;

type Coordinate = {
  latitude: number;
  longitude: number;
};

export type ExplorerPlanItem = {
  type: 'plan';
  id: string;
  plan: Plan;
  coordinate: Coordinate;
  profile: MapMarkerProfile;
};

export type ExplorerUserItem = {
  type: 'user';
  id: string;
  user: User;
  coordinate: Coordinate;
  profile: MapMarkerProfile;
};

export type ExplorerItem = ExplorerPlanItem | ExplorerUserItem;

type GetExplorerItemsParams = {
  plans: Plan[];
  users: User[];
  getPlanDisplayProfile: (plan: Plan) => MapMarkerProfile;
};

type GetVisibleExplorerItemsParams = {
  items: ExplorerItem[];
  region: MapRegion;
  pageSize?: number;
};

export function getExplorerItems({
  plans,
  users,
  getPlanDisplayProfile,
}: GetExplorerItemsParams): ExplorerItem[] {
  return [
    ...plans
      .filter((plan) => isValidMapCoordinate(plan.location))
      .map((plan) => ({
        type: 'plan' as const,
        id: `plan:${plan.planId}`,
        plan,
        coordinate: {
          latitude: plan.location.latitude,
          longitude: plan.location.longitude,
        },
        profile: getPlanDisplayProfile(plan),
      })),
    ...users
      .filter((user) => isValidMapCoordinate(user.hometown))
      .map((user) => ({
        type: 'user' as const,
        id: `user:${user.userId}`,
        user,
        coordinate: {
          latitude: user.hometown.latitude,
          longitude: user.hometown.longitude,
        },
        profile: {
          displayName: user.displayName,
          entityType: 'user' as const,
          photoUrl: user.photoUrl,
        },
      })),
  ];
}

export function getVisibleExplorerItems({
  items,
  region,
  pageSize = DEFAULT_PAGE_SIZE,
}: GetVisibleExplorerItemsParams) {
  const paddedRegion = expandRegion(region, VIEWPORT_PADDING_RATIO);
  const rankedVisibleItems = rankExplorerItems(
    items.filter((item) => coordinateInMapRegion(item.coordinate, paddedRegion)),
    region
  ).filter((item) => coordinateInMapRegion(item.coordinate, region));

  return limitPastPlanItems(rankedVisibleItems, pageSize);
}

export function rankExplorerItems(items: ExplorerItem[], region: MapRegion) {
  return [...items].sort((a, b) => getItemScore(b, region) - getItemScore(a, region));
}

export function getExplorerItemKey(item: ExplorerItem) {
  return item.id;
}

function getItemScore(item: ExplorerItem, region: MapRegion) {
  const proximityScore = getProximityScore(item.coordinate, region);
  const typeScore = item.type === 'plan' ? 0.2 : 0.1;
  const freshnessScore = item.type === 'plan' ? getPlanFreshnessScore(item.plan) : 0.15;

  return proximityScore * 0.5 + freshnessScore * 0.3 + typeScore;
}

function getProximityScore(coordinate: Coordinate, region: MapRegion) {
  const normalizedDistance = getCoordinateRegionDistanceRatio(coordinate, region);

  return Math.max(0, 1 - normalizedDistance / Math.sqrt(2));
}

function getPlanFreshnessScore(plan: Plan) {
  const now = Date.now();
  const startDateScore = getStartDateFreshnessScore(plan.startDate, now);
  const createdAtScore = getCreatedAtFreshnessScore(plan.createdAt, now);

  return (
    startDateScore * START_DATE_FRESHNESS_WEIGHT + createdAtScore * CREATED_AT_FRESHNESS_WEIGHT
  );
}

function limitPastPlanItems(items: ExplorerItem[], pastPlanLimit: number) {
  let pastPlanCount = 0;

  return items.filter((item) => {
    if (!isPastPlanItem(item)) return true;

    pastPlanCount += 1;
    return pastPlanCount <= pastPlanLimit;
  });
}

function isPastPlanItem(item: ExplorerItem) {
  return item.type === 'plan' && isDatePast(item.plan.endDate);
}

function getStartDateFreshnessScore(startDate: Date, now: number) {
  const startsInMs = startDate.getTime() - now;
  const daysFromNow = Math.abs(startsInMs) / DAY_IN_MS;

  return Math.max(0, 1 - daysFromNow / START_DATE_FRESHNESS_DAYS);
}

function getCreatedAtFreshnessScore(createdAt: Date, now: number) {
  const ageInMs = Math.max(0, now - createdAt.getTime());
  const daysOld = ageInMs / DAY_IN_MS;

  return Math.max(0, 1 - daysOld / CREATED_AT_FRESHNESS_DAYS);
}

function expandRegion(region: MapRegion, ratio: number): MapRegion {
  return {
    latitude: region.latitude,
    longitude: region.longitude,
    latitudeDelta: region.latitudeDelta * (1 + ratio * 2),
    longitudeDelta: region.longitudeDelta * (1 + ratio * 2),
  };
}
